// @flow

import nullthrows from 'sail/lib/nullthrows';

const documentBody = () => nullthrows(document.body);

/**
 * Copied from manage/frontend/src/platform/util/clipboard.js to be usable in docs.
 *
 * Programmatically copy the specified text to the user's clipboard.
 *
 * - Create a dummy span with the text contents, rendered off viewport.
 * - Use the Selection API to select the contents.
 * - Use `execCommand` to trigger a `copy` event on the selection.
 * - Set the clipboard data directly.
 * - Restore the original selection and remove the dummy node from the DOM.
 */
const copyToClipboard = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
    return;
  } catch (e) {
    // Either the browser doesn't support Clipboard.writeText(),
    // or this function wasn't called in a secure context.
    //
    // Fall back to the document.execCommand('copy') method.
  }

  const dummyNode = document.createElement('span');
  const selection = window.getSelection();
  const originalRange = selection.rangeCount
    ? selection.getRangeAt(0) || null
    : null;

  const handleCopy = (e: ClipboardEvent) => {
    const {clipboardData} = e;
    if (!clipboardData) {
      return;
    }

    try {
      clipboardData.setData('text/plain', text);
      e.preventDefault();
    } finally {
      // Restore the original selection range, if any.
      if (originalRange) {
        selection.removeAllRanges();
        selection.addRange(originalRange);
      }
      documentBody().removeChild(dummyNode);
      document.removeEventListener('copy', handleCopy);
    }
  };

  dummyNode.setAttribute('style', "position: 'absolute', left: '-10000px'");
  dummyNode.contentEditable = 'true';
  dummyNode.appendChild(document.createTextNode(text));
  documentBody().appendChild(dummyNode);

  const range = document.createRange();
  range.selectNodeContents(dummyNode);
  selection.removeAllRanges();
  selection.addRange(range);

  document.addEventListener('copy', handleCopy);
  document.execCommand('copy');
};

export {copyToClipboard};
